import React, { ReactElement } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from "react-router-dom";
import { useQuery } from 'react-query';

import {
  DataGrid,
  GridCellValue,
  GridColDef,
  GridPageChangeParams,
  GridSelectionModelChangeParams,
  GridSortModel,
  GridSortModelParams,
  ValueGetterParams,
} from '@material-ui/data-grid';

import { useEnergyApi } from '../_api/EnergyApiContext';
import RecRegistryCertificateBundle, { RecRegistryCertificateBundleRequest } from '../_api/data/RecRegistryCertificateBundle'
import { Paginated } from '../_api/data/pagination'
import SerialNumber from '../_api/data/SerialNumber'

function serialNumberColumnValue(params: ValueGetterParams): GridCellValue {
  return params.getValue('serialNumber')?.toString
}

function quantityColumnValue(params: ValueGetterParams): GridCellValue {
  const serialNumber = new SerialNumber(params.getValue('serialNumber'))
  return serialNumber.quantity.toLocaleString()
}

const columns: GridColDef[] = [
  { field: 'certificateTypeId', headerName: 'Certificate', width: 150 },
  { field: 'generationState', headerName: 'Generation State', width: 200 },
  { field: 'fuelSource', headerName: 'Fuel Source', width: 200 },
  { field: 'ownerAccountName', headerName: 'Owner', width: 200 },
  { field: 'serialNumberColumn', headerName: 'Serials', width: 150, valueGetter: serialNumberColumnValue },
  { field: 'quantityColumn', headerName: 'Quantity', width: 150, valueGetter: quantityColumnValue },
  { field: 'status', headerName: 'Status', width: 200 },
];

type Props = {
  generationId?: string
}

const RecRegistryCertificateBundlesDataTable: React.FC<Props> = ({ generationId }): React.ReactElement => {
  const defaultRequest = (): RecRegistryCertificateBundleRequest => {
    const request = new RecRegistryCertificateBundleRequest()
    request.generationId = generationId
    return request
  }

  const history = useHistory()
  const energyApi = useEnergyApi()
  const [request, setRequest] = React.useState(defaultRequest())

  const queryActions = (): Promise<Paginated<RecRegistryCertificateBundle>> => {
    return energyApi.getRecRegistryCertificateBundles(request)
  }

  const handlePageChange = (params: GridPageChangeParams) => {
    const newRequest = RecRegistryCertificateBundleRequest.copy(request)
    newRequest.page = params.page
    newRequest.per = params.pageSize
    setRequest(newRequest);
  };

  const setSortModel = (model: GridSortModel) => {
    const newRequest = RecRegistryCertificateBundleRequest.copy(request)
    newRequest.sort = model
    setRequest(newRequest)
  };

  const handleSortModelChange = (params: GridSortModelParams) => {
    if (params.sortModel !== request.sort) {
      setSortModel(params.sortModel);
    }
  };

  // Queries
  const {
    isLoading,
    isError,
    error,
    data,
    isFetching,
    isPreviousData,
  } = useQuery<Paginated<RecRegistryCertificateBundle>, Error>(['recRegistryCertificateBundles', request], (queryActions), { keepPreviousData: true });

  if (isLoading) {
    return <span>Loading...</span>
  }

  if (isError) {
    return <span>Error...</span>
  }

  if (data) {
    return (
      <div style={{ width: '100%' }}>
        <DataGrid
          rows={data.items}
          autoHeight={true}
          columns={columns}
          pagination
          pageSize={request.per}
          onPageSizeChange={handlePageChange}
          page={data.metadata.page}
          rowCount={data.metadata.total}
          paginationMode="server"
          sortingMode="server"
          onSortModelChange={handleSortModelChange}
          onPageChange={handlePageChange}
          loading={isLoading}
          disableColumnFilter
        />
      </div>
    )
  }

  return <span>Error</span>
};

RecRegistryCertificateBundlesDataTable.propTypes = {
  generationId: PropTypes.string,
}

export default RecRegistryCertificateBundlesDataTable;
